<template>
  <b-modal
    id="image-segmentation-preview-modal"
    size="xl"
    scrollable
    no-close-on-backdrop
    @show="onModalShow"
    @hide="onModalHide"
  >
    <template #modal-header="{ close }">
      <div class="d-flex align-items-center w-100">
        <h5 class="modal-title mb-0">
          {{ modalTitle }}
        </h5>
        <div class="ml-auto d-flex align-items-center header-toggles">
          <template v-if="currentStep === 'preview'">
            <b-form-checkbox
              v-if="removableBgLayerIds.length > 0"
              :checked="allRemoveBg"
              switch
              @change="toggleAllRemoveBg"
            >
              Remove background
            </b-form-checkbox>
            <b-form-checkbox
              v-if="convertibleLayerIds.length > 0"
              :checked="allConvertToText"
              switch
              @change="toggleAllConvertToText"
            >
              Convert to editable text
            </b-form-checkbox>
            <b-dropdown
              size="sm"
              variant="link"
              toggle-class="text-decoration-none text-muted px-2"
              no-caret
              right
              menu-class="segmentation-options-menu"
            >
              <template #button-content>
                <b-icon icon="three-dots-vertical" />
                <span class="sr-only">More options</span>
              </template>
              <b-dropdown-item @click.stop="showLayerDetails = !showLayerDetails">
                <b-icon
                  icon="check"
                  :class="{ invisible: !showLayerDetails }"
                  class="mr-1"
                />
                Show layer details
              </b-dropdown-item>
              <b-dropdown-item
                v-if="hasProductGroups"
                @click.stop="toggleGroupProducts"
              >
                <b-icon
                  icon="check"
                  :class="{ invisible: !groupProducts }"
                  class="mr-1"
                />
                Group side-by-side products
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <button
            v-if="!isImprovingAnyLayer"
            type="button"
            class="close"
            aria-label="Close"
            @click="close"
          >
            &times;
          </button>
        </div>
      </div>
    </template>

    <template #modal-footer>
      <div
        v-if="currentStep === 'select'"
        class="d-flex justify-content-between w-100"
      >
        <b-button
          variant="outline-secondary"
          @click="$bvModal.hide('image-segmentation-preview-modal')"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="!selectedImage"
          @click="startSegmentationFromSelection"
        >
          Segment Image
        </b-button>
      </div>

      <div
        v-else-if="currentStep === 'preview' && layers.length > 0"
        class="d-flex flex-column flex-md-row justify-content-between w-100"
      >
        <div class="d-flex flex-wrap">
          <b-button
            variant="outline-secondary"
            :disabled="isImprovingAnyLayer"
            @click="$bvModal.hide('image-segmentation-preview-modal')"
          >
            Cancel
          </b-button>
          <b-button
            v-if="canReanalyze"
            variant="outline-warning"
            class="ml-2"
            :disabled="isReanalyzing || isImprovingAnyLayer"
            @click="reanalyze"
          >
            <span v-if="isReanalyzing">
              <b-spinner
                small
                class="mr-1"
              />
              Re-analyzing...
            </span>
            <span v-else>
              <i class="fas fa-redo mr-1" />
              Re-analyze
            </span>
          </b-button>
        </div>
        <div class="d-flex flex-wrap mt-2 mt-md-0">
          <b-button
            variant="outline-primary"
            class="mr-2"
            @click="selectAllLayers"
          >
            Select All
          </b-button>
          <b-button
            variant="primary"
            :disabled="selectedLayerIds.length === 0 || isImprovingAnyLayer"
            @click="confirmSelection"
          >
            {{ confirmButtonText }}
          </b-button>
        </div>
      </div>

      <div
        v-else
        class="d-flex justify-content-end w-100"
      >
        <b-button
          variant="outline-secondary"
          @click="$bvModal.hide('image-segmentation-preview-modal')"
        >
          {{ hasError ? 'Close' : 'Cancel' }}
        </b-button>
      </div>
    </template>

    <!-- Image Selection Step -->
    <div
      v-if="currentStep === 'select'"
      class="py-3"
    >
      <p class="mb-3">
        Select an image from the file browser to segment into layers:
      </p>
      <FolderContentView
        :is-only-folder="true"
        :page-size="8"
        @onSelectedItems="handleSelectedItems"
      />
      <div class="form-group mt-3">
        <label for="layout-name-select">Layout Name</label>
        <b-form-input
          id="layout-name-select"
          v-model="layoutName"
          placeholder="Enter a name for the new layout"
        />
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-else-if="isProcessing"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem;"
        class="mb-3"
      />
      <h5>Analyzing image with AI...</h5>
      <p class="text-muted">
        This may take a minute. We're detecting layers, products, and background elements.
      </p>
      <b-progress
        :value="progressValue"
        :max="100"
        animated
        class="mt-3"
      >
        <b-progress-bar :value="progressValue">
          {{ progressValue }}%
        </b-progress-bar>
      </b-progress>
    </div>

    <!-- Error State -->
    <div
      v-else-if="hasError"
      class="text-center py-5"
    >
      <div class="text-danger mb-3">
        <i
          class="fas fa-exclamation-circle"
          style="font-size: 3rem;"
        />
      </div>
      <h5>Segmentation Failed</h5>
      <p class="text-muted">
        Unable to process the image. Please try again or select a different image.
      </p>
    </div>

    <!-- Preview State -->
    <div v-else-if="currentStep === 'preview' && layers.length > 0">
      <!-- Extraction Mode Toggle -->
      <div class="mb-3 p-3 bg-light rounded border">
        <label class="font-weight-bold mb-2 d-block">Extraction Mode</label>
        <b-form-radio-group
          v-model="extractionMode"
          :options="[
            { text: 'Full Segmentation', value: 'full' },
            { text: 'Product Only', value: 'product_only' },
          ]"
          buttons
          button-variant="outline-primary"
          size="sm"
        />
        <small
          v-if="isProductOnlyMode"
          class="d-block text-muted mt-2"
        >
          Only product layers will be extracted. Background will use the original image with product areas removed.
        </small>
        <small
          v-else
          class="d-block text-muted mt-2"
        >
          All detected layers (products, text, background) will be extracted separately.
        </small>
      </div>

      <p class="mb-3">
        We detected <strong>{{ visibleLayers.length }}</strong> layers in your image.
        Select the layers you want to include in your template:
      </p>

      <b-alert
        v-if="isStaleAnalysis"
        show
        variant="warning"
        class="mb-3"
      >
        <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
          <div>
            <strong>These layers are out of date</strong>
            <div>
              They were produced by an earlier version of the analysis. Re-analyzing picks up the
              latest improvements.
            </div>
          </div>
          <b-button
            variant="warning"
            class="mt-3 mt-md-0 ml-md-3 flex-shrink-0"
            :disabled="isReanalyzing || isImprovingAnyLayer"
            @click="reanalyze"
          >
            <b-spinner
              v-if="isReanalyzing"
              small
              class="mr-1"
            />
            Re-analyze
          </b-button>
        </div>
      </b-alert>

      <b-alert
        v-if="showImprovementAlert"
        show
        variant="info"
        class="mb-3"
      >
        <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
          <div>
            <strong>AI improvements available</strong>
            <div>
              Product image crops and background infill can be improved using AI.
            </div>
          </div>
          <b-button
            variant="primary"
            class="mt-3 mt-md-0 ml-md-3 flex-shrink-0"
            :disabled="isImprovingAnyLayer"
            @click="improveAllLayers"
          >
            <b-spinner
              v-if="isImprovingAll"
              small
              class="mr-1"
            />
            {{ isImprovingAll ? 'Improving...' : 'Improve all layers' }}
          </b-button>
        </div>
      </b-alert>

      <div class="row">
        <!-- Original Image Preview -->
        <div class="col-md-4 mb-3">
          <div class="card h-100">
            <div class="card-header">
              <strong>Original Image</strong>
            </div>
            <div class="card-body text-center">
              <img
                :src="originalImageUrl"
                alt="Original"
                class="img-fluid layer-preview"
              >
            </div>
          </div>
        </div>

        <!-- Inpainted Background (only show in full mode when no background layer was detected) -->
        <div
          v-if="inpaintedBackgroundUrl && !hasBackgroundLayer && !isProductOnlyMode"
          class="col-md-4 mb-3"
        >
          <div class="card h-100">
            <div class="card-header">
              <div class="d-flex justify-content-between align-items-center">
                <strong>Background (Inpainted)</strong>
                <span class="badge badge-info badge-pill">Included in Background</span>
              </div>
            </div>
            <div class="card-body text-center">
              <img
                :src="inpaintedBackgroundUrl"
                alt="Inpainted Background"
                class="img-fluid layer-preview"
              >
            </div>
          </div>
        </div>

        <!-- Detected Layers -->
        <div
          v-for="layer in visibleLayers"
          :key="layer.id"
          class="col-md-4 mb-3"
        >
          <div
            class="card h-100 layer-card"
            :class="{ 'border-primary': isLayerSelected(layer.id) }"
          >
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <strong>{{ formatLayerType(layer.type) }}</strong>
                <br>
                <small class="text-muted">{{ layer.description }}</small>
              </div>
              <b-form-checkbox
                :checked="isLayerSelected(layer.id)"
                switch
                @change="toggleLayer(layer.id)"
              />
            </div>
            <div class="card-body text-center layer-preview-container">
              <img
                :src="getShareUrl(layer.image_path || layer.preview_url)"
                :alt="layer.description"
                class="img-fluid layer-preview"
                :class="{ 'opacity-50': !isLayerSelected(layer.id) }"
              >
              <div
                v-show="showLayerDetails"
                class="layer-info mt-2"
              >
                <span class="badge badge-secondary">
                  z-index: {{ layer.z_index }}
                </span>
                <span
                  v-if="layer.confidence"
                  class="badge"
                  :class="getConfidenceBadgeClass(layer.confidence)"
                >
                  {{ layer.confidence }}% confidence
                </span>
              </div>
              <!-- Product options. Cutting the background out is what turns a crop into a
                   layer that can be moved around on its own, so it leads. -->
              <div
                v-if="layer.type === 'product' && isLayerSelected(layer.id)"
                class="mt-2"
              >
                <b-form-checkbox
                  :checked="isRemoveBg(layer.id)"
                  size="sm"
                  @change="toggleRemoveBg(layer.id)"
                >
                  Remove background
                </b-form-checkbox>
                <b-form-checkbox
                  :checked="hasOvalShadow(layer.id)"
                  size="sm"
                  class="mt-1"
                  @change="toggleOvalShadow(layer.id)"
                >
                  Add oval shadow
                </b-form-checkbox>
              </div>
              <!-- Convert to editable text / Remove background options (mutually exclusive) -->
              <div
                v-if="layer.type === 'text_image' && isLayerSelected(layer.id) && !isProductOnlyMode"
                class="mt-2"
              >
                <b-form-checkbox
                  :checked="isConvertToText(layer.id)"
                  size="sm"
                  @change="toggleConvertToText(layer.id)"
                >
                  Convert to editable text
                </b-form-checkbox>
                <b-form-checkbox
                  :checked="isRemoveBg(layer.id)"
                  size="sm"
                  class="mt-1"
                  @change="toggleRemoveBg(layer.id)"
                >
                  Remove background
                </b-form-checkbox>
              </div>
            </div>
            <div
              v-if="layer.type === 'background' || layer.type === 'product'"
              class="card-footer d-flex align-items-center layer-retry-row"
            >
              <b-form-input
                :value="guidanceFor(layer.id)"
                size="sm"
                class="mr-2 layer-guidance-input"
                :placeholder="guidancePlaceholder(layer)"
                :maxlength="guidanceMaxLength"
                :disabled="isImprovingLayer(layer.id)"
                :title="guidanceFor(layer.id)"
                @click.stop
                @input="setGuidance(layer.id, $event)"
                @keyup.enter="improveLayer(layer)"
              />
              <b-button
                variant="outline-primary"
                size="sm"
                class="text-nowrap"
                :disabled="isImprovingLayer(layer.id)"
                @click.stop="improveLayer(layer)"
              >
                <b-spinner
                  v-if="isImprovingLayer(layer.id)"
                  small
                  class="mr-1"
                />
                <b-icon
                  v-else
                  icon="arrow-repeat"
                  class="mr-1"
                />
                {{ getImproveButtonText(layer) }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else
      class="text-center py-5"
    >
      <p class="text-muted">
        No layers detected yet. Start by selecting an image to segment.
      </p>
    </div>

    <!-- Creating Layout Overlay -->
    <b-overlay
      :show="isCreatingLayout"
      no-wrap
    >
      <template #overlay>
        <div class="text-center d-flex flex-column align-items-center justify-content-center">
          <b-spinner
            variant="primary"
            class="mb-2"
          />
          <p class="mb-0">
            Creating layout from layers...
          </p>
        </div>
      </template>
    </b-overlay>
  </b-modal>
</template>

<script>
import imageSegmentationApi from '@frontend/services/api/image-segmentation';
import {
  imageSegmentationEventBroker,
  IMAGE_SEGMENTATION_EVENTS
} from '@frontend/group/modules/image-segmentation/event-broker';
import { cacheSegmentationResult } from '@frontend/group/modules/create-layers/index';
import { segmentationQueue } from '@frontend/group/modules/image-segmentation/queue';
import FolderContentView from '@frontend/components/modules/file-browser/FolderContentView.vue';

export default {
  name: 'ImageSegmentationPreviewModal',

  components: {
    FolderContentView,
  },

  data() {
    return {
      currentStep: 'select', // 'select', 'processing', 'preview'
      isProcessing: false,
      isCreatingLayout: false,
      isReanalyzing: false,
      isCachedResult: false, // True when showing cached results
      hasError: false,
      errorMessage: '',
      progressValue: 0,
      jobUuid: null,
      customerId: null,
      sourceLayoutId: null, // Layout ID when adding template to existing layout
      sourceInstanceId: null, // Template instance ID for caching
      sourceImagePath: '', // Current image path (may be inpainted)
      originalSegmentedImagePath: '', // Original image that was segmented (for re-analysis)
      originalImageUrl: '',
      inpaintedBackgroundUrl: '',
      layers: [],
      selectedLayerIds: [],
      includeBackground: true,
      layoutName: '',
      pollInterval: null,
      selectedImage: null,
      displayedWidth: null, // Template dimensions for re-analyze
      displayedHeight: null,
      convertToTextLayerIds: [], // Layer IDs to convert from text_image to editable text
      removeBgLayerIds: [], // Layer IDs to remove background from
      ovalShadowLayerIds: [], // Product layer IDs that should receive an oval shadow
      extractionMode: 'full', // 'product_only' or 'full'
      improvingLayerIds: [],
      isImprovingAll: false,
      improvementAlertDismissed: false,
      showLayerDetails: false, // z-index / confidence badges on the layer cards
      groupProducts: false, // Show products that stand in a row as one layer instead of several
      layerGuidance: {}, // Per-layer note steering a retry, keyed by layer id
      isStaleAnalysis: false, // These layers came from an older analysis and are worth redoing
      queuePosition: 0, // Which image of a multi-image import is on screen, 0 when there is no queue
      queueTotal: 0,
      queueImageName: '',
    };
  },

  computed: {
    modalTitle() {
      if (this.currentStep === 'select') {
        return 'Create Layers from Image';
      }
      if (this.isProcessing) {
        return `Segmenting Image${this.queueLabel}...`;
      }
      if (this.hasError) {
        return 'Segmentation Error';
      }
      return `Select Layers to Import${this.queueLabel}`;
    },

    /** " (2 of 5)", or nothing at all when a single image is being imported. */
    queueLabel() {
      if (this.queueTotal < 2) {
        return '';
      }
      return ` (${this.queuePosition} of ${this.queueTotal})`;
    },

    confirmButtonText() {
      const count = this.selectedLayerIds.length;
      if (this.queueTotal >= 2) {
        return this.queuePosition < this.queueTotal
          ? `Next Image (${count} Layer${count !== 1 ? 's' : ''})`
          : `Create Layout with ${this.queueTotal} Images`;
      }
      if (this.sourceLayoutId) {
        return `Add ${count} Layer${count !== 1 ? 's' : ''} as New Template`;
      }
      return `Create Layout with ${count} Layer${count !== 1 ? 's' : ''}`;
    },

    hasBackgroundLayer() {
      return this.layers.some(layer => layer.type === 'background');
    },

    visibleLayers() {
      const layers = this.extractionMode === 'product_only'
        ? this.layers.filter(layer => layer.type === 'product')
        : this.layers;

      // A grouped row and the products inside it are two readings of the same pixels, so only
      // one of them is ever on screen.
      return this.groupProducts
        ? layers.filter(layer => !this.groupedMemberIds.includes(layer.id))
        : layers.filter(layer => !layer.is_group);
    },

    isProductOnlyMode() {
      return this.extractionMode === 'product_only';
    },

    isImprovingAnyLayer() {
      return this.improvingLayerIds.length > 0;
    },

    layersToImprove() {
      // Only what is on screen: a grouped row and its members cover the same pixels, so
      // improving both would pay for the same work twice.
      return this.visibleLayers.filter(layer => (
        layer.type === 'background' || layer.type === 'product'
      ) && !this.isImprovedLayer(layer));
    },

    showImprovementAlert() {
      return !this.improvementAlertDismissed && this.layersToImprove.length > 0;
    },

    convertibleLayerIds() {
      if (this.isProductOnlyMode) return [];

      return this.visibleLayers
        .filter(layer => layer.type === 'text_image' && this.isLayerSelected(layer.id))
        .map(layer => layer.id);
    },

    allConvertToText() {
      return this.convertibleLayerIds.length > 0
        && this.convertibleLayerIds.every(id => this.isConvertToText(id));
    },

    /**
     * Layers whose background can be cut away. Products as well as text: a product crop is a
     * rectangle of the picture it came from, so without this it arrives in the template still
     * carrying the backdrop behind it and cannot be moved around on its own. That is truest of
     * a grouped row, which is one wide crop spanning everything between the outer two packs.
     */
    removableBgLayerIds() {
      return this.visibleLayers
        .filter(layer => this.isLayerSelected(layer.id)
          && (layer.type === 'product'
            || (layer.type === 'text_image' && !this.isProductOnlyMode)))
        .map(layer => layer.id);
    },

    allRemoveBg() {
      return this.removableBgLayerIds.length > 0
        && this.removableBgLayerIds.every(id => this.isRemoveBg(id));
    },

    /**
     * Whether the whole image can be analysed again. Offered whenever there is an image to
     * work from, not only for results that came out of the cache: an analysis that read the
     * picture wrongly is worth redoing however it arrived, and the alternative is cancelling
     * and importing the image a second time.
     */
    canReanalyze() {
      return Boolean(this.originalSegmentedImagePath || this.sourceImagePath);
    },

    /** Kept in step with ImageSegmentationService::GUIDANCE_MAX_LENGTH. */
    guidanceMaxLength() {
      return 500;
    },

    /** Whether the analysis found any row of products to offer as a single layer. */
    hasProductGroups() {
      return this.layers.some(layer => layer.is_group);
    },

    /** Layers covered by a group, so they can be hidden while the group is shown instead. */
    groupedMemberIds() {
      return this.layers
        .filter(layer => layer.is_group)
        .reduce((ids, layer) => ids.concat(layer.group_member_ids || []), []);
    },
  },

  watch: {
    extractionMode() {
      // When mode changes, select all visible layers for the new mode
      this.selectDefaultLayers();
      // Clear convert-to-text and remove-bg selections when switching to product_only
      if (this.extractionMode === 'product_only') {
        this.convertToTextLayerIds = [];
        this.removeBgLayerIds = [];
      }
    },
  },

  mounted() {
    // Add click handler for "Create Layers from Image" button
    const createLayersBtn = document.querySelector('.create-layers-btn');
    if (createLayersBtn) {
      createLayersBtn.addEventListener('click', () => {
        this.openForImageSelection();
      });
    }

    // Listen for show preview events
    imageSegmentationEventBroker.on(
      IMAGE_SEGMENTATION_EVENTS.SHOW_PREVIEW,
      this.handleShowPreview,
      true
    );

    // Listen for show cached preview events (no re-parsing needed)
    imageSegmentationEventBroker.on(
      IMAGE_SEGMENTATION_EVENTS.SHOW_CACHED_PREVIEW,
      this.handleShowCachedPreview,
      true
    );

    // Listen for job completed events (from WebSocket)
    imageSegmentationEventBroker.on(
      IMAGE_SEGMENTATION_EVENTS.JOB_COMPLETED,
      this.handleJobCompleted,
      true
    );

    // Listen for job failed events
    imageSegmentationEventBroker.on(
      IMAGE_SEGMENTATION_EVENTS.JOB_FAILED,
      this.handleJobFailed,
      true
    );

    // A multi-image import that cannot build its layout leaves the confirm button spinning,
    // because nothing navigates away from the modal in that case.
    imageSegmentationEventBroker.on(
      IMAGE_SEGMENTATION_EVENTS.QUEUE_FAILED,
      this.handleQueueFailed,
      true
    );
  },

  beforeDestroy() {
    this.cleanup();
    imageSegmentationEventBroker.off(
      IMAGE_SEGMENTATION_EVENTS.SHOW_PREVIEW,
      this.handleShowPreview
    );
    imageSegmentationEventBroker.off(
      IMAGE_SEGMENTATION_EVENTS.SHOW_CACHED_PREVIEW,
      this.handleShowCachedPreview
    );
    imageSegmentationEventBroker.off(
      IMAGE_SEGMENTATION_EVENTS.JOB_COMPLETED,
      this.handleJobCompleted
    );
    imageSegmentationEventBroker.off(
      IMAGE_SEGMENTATION_EVENTS.JOB_FAILED,
      this.handleJobFailed
    );
    imageSegmentationEventBroker.off(
      IMAGE_SEGMENTATION_EVENTS.QUEUE_FAILED,
      this.handleQueueFailed
    );
  },

  methods: {
    handleShowPreview(data) {
      // Start from a clean slate. On a first open this changes nothing; when a queue points
      // the modal at its next image it is what clears the previous image's selections.
      this.cleanup();
      this.improvementAlertDismissed = false;
      this.queuePosition = data.queuePosition || 0;
      this.queueTotal = data.queueTotal || 0;
      this.queueImageName = data.queueImageName || '';
      this.jobUuid = data.jobUuid;
      this.customerId = data.customerId;
      this.sourceLayoutId = data.layoutId || null; // Store source layout ID if adding to existing
      this.sourceInstanceId = data.instanceId || null; // Store instance ID for caching
      this.sourceImagePath = data.imagePath || data.originalImageUrl || '';
      this.originalImageUrl = this.getShareUrl(data.originalImageUrl || data.imagePath || '');
      this.layoutName = data.layoutName || '';
      this.displayedWidth = data.displayedWidth || null; // Store dimensions for re-analyze
      this.displayedHeight = data.displayedHeight || null;
      this.currentStep = 'processing';
      this.isProcessing = true;
      this.progressValue = 10;

      this.$bvModal.show('image-segmentation-preview-modal');

      // Start polling for job status
      this.startPolling();
    },

    handleShowCachedPreview(data) {
      // Show preview instantly from cached data - no re-parsing needed
      this.cleanup();
      this.improvementAlertDismissed = false;
      this.queuePosition = data.queuePosition || 0;
      this.queueTotal = data.queueTotal || 0;
      this.queueImageName = data.queueImageName || '';
      this.jobUuid = data.jobUuid;
      this.customerId = data.customerId;
      this.sourceLayoutId = data.layoutId || null;
      this.sourceInstanceId = data.instanceId || null;
      this.sourceImagePath = data.imagePath || ''; // Current image path (may be inpainted)
      this.originalSegmentedImagePath = data.originalImagePath || data.imagePath || ''; // Original image for re-analysis
      this.originalImageUrl = this.getShareUrl(data.originalImagePath || data.imagePath || '');
      this.layoutName = '';
      this.isCachedResult = true; // Mark as cached result
      this.isStaleAnalysis = data.stale === true;
      this.displayedWidth = data.displayedWidth || null; // Store dimensions for re-analyze
      this.displayedHeight = data.displayedHeight || null;

      // Use cached data directly
      const cachedData = data.cachedData;
      this.layers = cachedData.layers || [];
      this.inpaintedBackgroundUrl = cachedData.inpaintedBackgroundUrl || '';

      // Select layers appropriate for current extraction mode
      this.selectDefaultLayers();
      this.selectSuggestedOvalShadows();

      this.currentStep = 'preview';
      this.isProcessing = false;
      this.progressValue = 100;

      this.$bvModal.show('image-segmentation-preview-modal');

      // Show info that we're using cached results
      toastr.info('Using previously analyzed layers', 'Cached Results');
    },

    handleJobCompleted(data) {
      // Support both 'job_uuid' (from Pusher) and 'uuid' (from polling)
      const dataJobUuid = data.job_uuid || data.uuid;
      if (dataJobUuid !== this.jobUuid) return;

      this.currentStep = 'preview';
      this.isProcessing = false;
      this.progressValue = 100;
      this.layers = data.layer_data || [];
      this.inpaintedBackgroundUrl = data.inpainted_background_path || '';
      this.improvementAlertDismissed = false;

      // Select layers appropriate for current extraction mode
      this.selectDefaultLayers();
      this.selectSuggestedOvalShadows();

      // Cache the results for this template instance
      if (this.sourceInstanceId && this.sourceImagePath) {
        cacheSegmentationResult(
          this.sourceInstanceId,
          this.sourceImagePath,
          this.jobUuid,
          {
            layers: this.layers,
            inpaintedBackgroundUrl: this.inpaintedBackgroundUrl,
          }
        );
      }

      // Show success notification
      const layerCount = this.visibleLayers.length;
      toastr.success(`Found ${layerCount} layer${layerCount !== 1 ? 's' : ''} in your image`, 'Segmentation Complete');

      this.stopPolling();
    },

    handleQueueFailed() {
      this.isCreatingLayout = false;
    },

    handleJobFailed(data) {
      // Support both 'job_uuid' (from Pusher) and 'uuid' (from polling)
      const dataJobUuid = data.job_uuid || data.uuid;
      if (dataJobUuid !== this.jobUuid) return;

      this.isProcessing = false;
      this.hasError = true;
      // Store detailed error for console logging only
      console.error('Segmentation failed:', data.error_message);

      // Show generic error to user (no technical details for security)
      toastr.error('Unable to process the image. Please try again.', 'Segmentation Failed');

      this.stopPolling();
    },

    startPolling() {
      if (this.pollInterval) return;

      // Progress simulation parameters
      // The new gemini-3-pro model takes longer (~2-3 minutes)
      // So we slow down the progress to better match reality
      let pollCount = 0;

      this.pollInterval = setInterval(async () => {
        try {
          const response = await imageSegmentationApi.getJobStatus(this.jobUuid);
          const job = response.data;
          pollCount++;

          // Logarithmic progress simulation - fast at first, then slows down
          // This better reflects the actual processing stages
          if (this.progressValue < 90) {
            // Progress more slowly as we get higher
            const increment = Math.max(1, Math.floor(10 / Math.sqrt(pollCount)));
            this.progressValue = Math.min(90, this.progressValue + increment);
          }

          if (job.status === 'completed') {
            this.handleJobCompleted(job);
          } else if (job.status === 'failed') {
            this.handleJobFailed(job);
          }
        } catch (error) {
          console.error('Error polling job status:', error);
        }
      }, 4000); // Poll every 4 seconds instead of 3
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    isLayerSelected(layerId) {
      return this.selectedLayerIds.includes(layerId);
    },

    toggleLayer(layerId) {
      if (this.isLayerSelected(layerId)) {
        this.selectedLayerIds = this.selectedLayerIds.filter(id => id !== layerId);
        // Also remove from convert-to-text and remove-bg if deselected
        this.convertToTextLayerIds = this.convertToTextLayerIds.filter(id => id !== layerId);
        this.removeBgLayerIds = this.removeBgLayerIds.filter(id => id !== layerId);
        this.ovalShadowLayerIds = this.ovalShadowLayerIds.filter(id => id !== layerId);
      } else {
        this.selectedLayerIds.push(layerId);
      }
    },

    isConvertToText(layerId) {
      return this.convertToTextLayerIds.includes(layerId);
    },

    hasOvalShadow(layerId) {
      return this.ovalShadowLayerIds.includes(layerId);
    },

    toggleOvalShadow(layerId) {
      if (this.hasOvalShadow(layerId)) {
        this.ovalShadowLayerIds = this.ovalShadowLayerIds.filter(id => id !== layerId);
      } else {
        this.ovalShadowLayerIds.push(layerId);
      }
    },

    toggleConvertToText(layerId) {
      if (this.isConvertToText(layerId)) {
        this.convertToTextLayerIds = this.convertToTextLayerIds.filter(id => id !== layerId);
      } else {
        this.convertToTextLayerIds.push(layerId);
        // Mutually exclusive: remove from remove-bg
        this.removeBgLayerIds = this.removeBgLayerIds.filter(id => id !== layerId);
      }
    },

    toggleAllConvertToText(checked) {
      const ids = this.convertibleLayerIds;

      if (checked) {
        this.convertToTextLayerIds = [
          ...this.convertToTextLayerIds,
          ...ids.filter(id => !this.isConvertToText(id)),
        ];
        this.removeBgLayerIds = this.removeBgLayerIds.filter(id => !ids.includes(id));
        return;
      }

      this.convertToTextLayerIds = this.convertToTextLayerIds.filter(id => !ids.includes(id));
    },

    toggleAllRemoveBg(checked) {
      const ids = this.removableBgLayerIds;

      if (checked) {
        this.removeBgLayerIds = [
          ...this.removeBgLayerIds,
          ...ids.filter(id => !this.isRemoveBg(id)),
        ];
        // Mutually exclusive with convert-to-text, the same way the per-layer boxes are.
        this.convertToTextLayerIds = this.convertToTextLayerIds.filter(id => !ids.includes(id));
        return;
      }

      this.removeBgLayerIds = this.removeBgLayerIds.filter(id => !ids.includes(id));
    },

    /** What the user typed to steer this layer's retry. */
    guidanceFor(layerId) {
      return this.layerGuidance[layerId] || '';
    },

    setGuidance(layerId, value) {
      this.$set(this.layerGuidance, layerId, value);
    },

    toggleGroupProducts() {
      this.groupProducts = !this.groupProducts;
      // The cards on screen change, so the selection has to follow them.
      this.selectDefaultLayers();
    },

    isRemoveBg(layerId) {
      return this.removeBgLayerIds.includes(layerId);
    },

    toggleRemoveBg(layerId) {
      if (this.isRemoveBg(layerId)) {
        this.removeBgLayerIds = this.removeBgLayerIds.filter(id => id !== layerId);
      } else {
        this.removeBgLayerIds.push(layerId);
        // Mutually exclusive: remove from convert-to-text
        this.convertToTextLayerIds = this.convertToTextLayerIds.filter(id => id !== layerId);
      }
    },

    selectAllLayers() {
      this.selectedLayerIds = this.visibleLayers.map(layer => layer.id);
      this.includeBackground = true;
    },

    selectDefaultLayers() {
      // visibleLayers already accounts for the extraction mode and for whether rows of
      // products are shown grouped, so selecting all of it is the right default either way.
      this.selectedLayerIds = this.visibleLayers.map(layer => layer.id);
    },

    selectSuggestedOvalShadows() {
      this.ovalShadowLayerIds = this.layers
        .filter(layer => layer.type === 'product' && layer.suggest_oval_shadow === true)
        .map(layer => layer.id);
    },

    formatLayerType(type) {
      const typeLabels = {
        product: 'Product',
        background: 'Background',
        text: 'Text',
        text_image: 'Text / Logo',
        logo: 'Logo',
        decoration: 'Decoration',
        graphic: 'Graphic Element',
      };
      return typeLabels[type] || type;
    },

    getConfidenceBadgeClass(confidence) {
      if (confidence >= 80) return 'badge-success';
      if (confidence >= 60) return 'badge-warning';
      return 'badge-danger';
    },

    isImprovingLayer(layerId) {
      return this.improvingLayerIds.includes(layerId);
    },

    isImprovedLayer(layer) {
      return layer.improved || /(^|\/)images\/uploads\/inpaint\//.test(layer.image_path || '');
    },

    guidancePlaceholder(layer) {
      return layer.type === 'product'
        ? 'Optional guidance, e.g. keep the cap visible'
        : 'Optional guidance, e.g. remove the icons too';
    },

    getImproveButtonText(layer) {
      // One label in both states. The note beside it makes this a "run it again, like this"
      // control, and calling it Improve the first time only asks the user to notice a
      // distinction that does not change what the button does.
      return this.isImprovingLayer(layer.id) ? 'Retrying...' : 'Retry';
    },

    async improveLayer(layer) {
      if (!this.jobUuid || this.isImprovingLayer(layer.id)) return;

      this.improvingLayerIds.push(layer.id);

      try {
        const guidance = this.guidanceFor(layer.id).trim() || null;
        const request = layer.type === 'product'
          ? imageSegmentationApi.improveProduct(this.jobUuid, layer.id, guidance)
          : imageSegmentationApi.improveBackground(this.jobUuid, layer.id, guidance);
        const { data } = await request;
        const layerIndex = this.layers.findIndex(item => item.id === layer.id);

        if (!data.success || !data.layer || layerIndex === -1) {
          throw new Error('The improved layer response was incomplete.');
        }

        const improvedLayerUrl = this.getShareUrl(data.layer.image_path || '');
        const improvedLayer = {
          ...data.layer,
          preview_url: improvedLayerUrl,
        };

        this.$set(this.layers, layerIndex, improvedLayer);
        if (layer.type === 'product' && data.suggest_oval_shadow && !this.hasOvalShadow(layer.id)) {
          this.ovalShadowLayerIds.push(layer.id);
        }
        if (layer.type === 'background') {
          this.inpaintedBackgroundUrl = this.getShareUrl(
            data.inpainted_background_path || data.layer.image_path || ''
          );
        }

        if (this.sourceInstanceId && this.sourceImagePath) {
          cacheSegmentationResult(
            this.sourceInstanceId,
            this.sourceImagePath,
            this.jobUuid,
            {
              layers: this.layers,
              inpaintedBackgroundUrl: this.inpaintedBackgroundUrl,
            },
            this.isStaleAnalysis
          );
        }

        toastr.success(`${layer.type === 'product' ? 'Product' : 'Background'} improved successfully!`);
      } catch (error) {
        console.error('Failed to improve layer:', error);
        const layerName = layer.type === 'product' ? 'product' : 'background';
        toastr.error(error.response?.data?.message || `Failed to improve the ${layerName}. Please try again.`);
      } finally {
        this.improvingLayerIds = this.improvingLayerIds.filter(id => id !== layer.id);
      }
    },

    async improveAllLayers() {
      if (this.isImprovingAnyLayer || this.layersToImprove.length === 0) return;

      this.isImprovingAll = true;

      try {
        await Promise.all(this.layersToImprove.map(layer => this.improveLayer(layer)));
      } finally {
        this.isImprovingAll = false;
        this.improvementAlertDismissed = true;
      }
    },

    async confirmSelection() {
      if (this.selectedLayerIds.length === 0) {
        toastr.warning('Please select at least one layer');
        return;
      }

      const modalBody = document.querySelector('#image-segmentation-preview-modal .modal-body');
      if (modalBody) {
        modalBody.scrollTop = 0;
      }
      this.isCreatingLayout = true;

      // A multi-image import collects every image's choices first and builds the layout once,
      // at the end, so that closing the modal half way through leaves nothing behind.
      if (segmentationQueue.isActive()) {
        try {
          await segmentationQueue.collect({
            jobUuid: this.jobUuid,
            selectedLayerIds: [...this.selectedLayerIds],
            convertToTextLayerIds: [...this.convertToTextLayerIds],
            removeBgLayerIds: [...this.removeBgLayerIds],
            ovalShadowLayerIds: [...this.ovalShadowLayerIds],
            extractionMode: this.extractionMode,
          });
        } catch (error) {
          console.error('Error while importing the selected images:', error);
          toastr.error('Something went wrong while importing the selected images');
          this.isCreatingLayout = false;
        }
        return;
      }

      try {
        // Pass sourceLayoutId to control whether to create new layout or add to existing
        // null = create new layout, number = add to that layout
        const response = await imageSegmentationApi.createFromSegmentation(
          this.customerId || 0,
          this.jobUuid,
          this.selectedLayerIds,
          this.layoutName,
          this.sourceLayoutId, // Will be null from File Browser, or layout ID from Advanced menu
          this.convertToTextLayerIds, // Layer IDs to convert to editable text
          this.extractionMode, // 'product_only' or 'full'
          this.removeBgLayerIds, // Layer IDs to remove background from
          this.ovalShadowLayerIds // Product layer IDs that should receive an oval shadow
        );

        if (response.data && response.data.success) {
          // Fire confirmation event
          imageSegmentationEventBroker.fire(IMAGE_SEGMENTATION_EVENTS.LAYERS_CONFIRMED, {
            layoutId: response.data.layout_id,
            customerId: response.data.customer_id,
          });

          // Check if we added a template to the current layout (reload) or created a new one (redirect)
          if (this.sourceLayoutId && response.data.layout_id == this.sourceLayoutId) {
            // Added template to existing layout - reload to show the new template
            toastr.success('Template with layers added successfully!');
            window.location.reload();
          } else {
            // Created a new layout - redirect to it
            toastr.success('Layout created successfully!');
            if (response.data.redirect_url) {
              window.location.href = response.data.redirect_url;
            } else {
              window.location.href = `/banner/${response.data.customer_id}/group/${response.data.layout_id}/show`;
            }
          }
        } else {
          toastr.error(response.data?.message || 'Failed to create layout');
          this.isCreatingLayout = false;
        }
      } catch (error) {
        console.error('Error creating layout from segmentation:', error);
        toastr.error(error.response?.data?.message || 'Failed to create layout');
        this.isCreatingLayout = false;
      }
    },

    async reanalyze() {
      // Use the original segmented image path, not the current template's background
      const imagePathForReanalysis = this.originalSegmentedImagePath || this.sourceImagePath;

      if (!imagePathForReanalysis) {
        toastr.error('Cannot re-analyze: original image path not found');
        return;
      }

      this.isReanalyzing = true;

      try {
        // Start a new segmentation job using the ORIGINAL image, forcing re-analysis (ignoring cache)
        // Pass the stored dimensions so the new template has the same size
        const { data } = await imageSegmentationApi.startSegmentation(
          this.customerId,
          imagePathForReanalysis,
          this.sourceLayoutId,
          null, // layoutName
          this.displayedWidth, // Pass stored dimensions for correct template sizing
          this.displayedHeight,
          null, // No job UUID - force new analysis
          true // Force re-analyze flag
        );

        if (data.job_uuid) {
          this.jobUuid = data.job_uuid;
          this.isCachedResult = false;
          this.currentStep = 'processing';
          this.isProcessing = true;
          this.progressValue = 10;
          this.isReanalyzing = false;

          // Start polling for job status
          this.startPolling();

          toastr.info('Re-analyzing image...', 'New Analysis Started');
        } else {
          toastr.error('Failed to start re-analysis');
          this.isReanalyzing = false;
        }
      } catch (e) {
        toastr.error(e.response?.data?.message || 'Something went wrong while re-analyzing');
        console.error('Re-analyze error:', e);
        this.isReanalyzing = false;
      }
    },

    onModalShow() {
      // Modal opened
    },

    onModalHide(event) {
      if (this.isImprovingAnyLayer) {
        event.preventDefault();
        return;
      }

      this.cleanup();
      imageSegmentationEventBroker.fire(IMAGE_SEGMENTATION_EVENTS.CLOSED, {});
    },

    cleanup() {
      this.stopPolling();
      this.currentStep = 'closed';
      this.isProcessing = false;
      this.isCreatingLayout = false;
      this.isReanalyzing = false;
      this.isCachedResult = false;
      this.hasError = false;
      this.errorMessage = '';
      this.progressValue = 0;
      this.layers = [];
      this.selectedLayerIds = [];
      this.inpaintedBackgroundUrl = '';
      this.selectedImage = null;
      this.layoutName = '';
      this.convertToTextLayerIds = [];
      this.removeBgLayerIds = [];
      this.ovalShadowLayerIds = [];
      this.extractionMode = 'full';
      this.improvingLayerIds = [];
      this.isImprovingAll = false;
      this.improvementAlertDismissed = false;
      this.showLayerDetails = false;
      this.sourceLayoutId = null;
      this.sourceInstanceId = null;
      this.sourceImagePath = '';
      this.originalSegmentedImagePath = '';
      this.groupProducts = false;
      this.layerGuidance = {};
      this.isStaleAnalysis = false;
      this.queuePosition = 0;
      this.queueTotal = 0;
      this.queueImageName = '';
    },

    openForImageSelection() {
      this.cleanup();
      this.currentStep = 'select';
      this.$bvModal.show('image-segmentation-preview-modal');
    },

    handleSelectedItems(items) {
      // Filter to only images
      const images = items.filter(item => item.type === 'image');
      this.selectedImage = images.length > 0 ? images[0] : null;
    },

    async startSegmentationFromSelection() {
      if (!this.selectedImage) {
        toastr.warning('Please select an image');
        return;
      }

      // Get the image path
      let imagePath = this.selectedImage.path;
      if (/share\?file=/.test(imagePath)) {
        const url = new URL(imagePath);
        imagePath = url.searchParams.get('file');
      }

      // Get customerId from current context or use 0
      const customerId = window.Context?.customerId || 0;

      try {
        this.currentStep = 'processing';
        this.isProcessing = true;
        this.progressValue = 10;

        // Start segmentation job
        const { data } = await imageSegmentationApi.startSegmentation(
          customerId,
          imagePath,
          null,
          this.layoutName || null
        );

        console.log('File Browser: API response', { imagePath, data });

        if (data.job_uuid) {
          this.jobUuid = data.job_uuid;
          this.customerId = customerId;
          this.originalImageUrl = this.getShareUrl(imagePath);

          // Check if we got cached results from the backend (persistent cache)
          if (data.cached && data.layer_data) {
            console.log('File Browser: Using cached results', data);
            // Use cached results - instantly show preview
            this.layers = data.layer_data || [];
            this.inpaintedBackgroundUrl = data.inpainted_background_path || '';
            this.selectDefaultLayers();
            this.selectSuggestedOvalShadows();
            this.sourceImagePath = imagePath; // Current image path
            this.originalSegmentedImagePath = data.original_image_path || imagePath; // Original image for re-analysis
            this.originalImageUrl = this.getShareUrl(data.original_image_path || imagePath);
            this.isCachedResult = true; // Mark as cached result
            this.isStaleAnalysis = data.stale === true;
            this.currentStep = 'preview';
            this.isProcessing = false;
            this.progressValue = 100;

            // Show toastr after DOM updates to ensure it's visible
            this.$nextTick(() => {
              toastr.info('Using previously analyzed layers', 'Cached Results');
            });
          } else {
            console.log('File Browser: Starting new analysis (no cache)', data);
            // Start polling for job status
            this.startPolling();
          }
        } else {
          toastr.error('Failed to start image segmentation');
          this.currentStep = 'select';
          this.isProcessing = false;
        }
      } catch (e) {
        toastr.error(e.response?.data?.message || 'Something went wrong while starting segmentation');
        console.error(e);
        this.currentStep = 'select';
        this.isProcessing = false;
      }
    },

    /**
     * Convert a raw file path to a share URL for display.
     * @param {string} path - Raw file path (e.g., "user-folder/123/user-files/image.png")
     * @returns {string} Share URL (e.g., "/share?file=user-folder/123/user-files/image.png")
     */
    getShareUrl(path) {
      if (!path) return '';
      // If already a full URL or share URL, return as-is
      if (path.startsWith('http') || path.startsWith('/share')) {
        return path;
      }
      // If it's a local storage path, return as-is
      if (path.startsWith('/storage/')) {
        return path;
      }
      // Convert raw S3 path to share URL
      const cleanPath = path.replace(/^\//, ''); // Remove leading slash if present
      return `/share?file=${encodeURIComponent(cleanPath)}`;
    },
  },
};
</script>

<style scoped lang="scss">
.layer-retry-row {
  // Its own strip under the preview: the preview sits on a transparency checkerboard, and a
  // control standing on that reads as see-through rather than as a control.
  background-color: #fff;
  min-width: 0;
}

.layer-guidance-input {
  min-width: 0;
  flex: 1 1 auto;

  &::placeholder {
    font-size: 0.8125rem;
  }
}

.segmentation-options-menu {
  // Checkmarks sit in a fixed gutter so the labels line up whether or not they are ticked.
  min-width: 15rem;
}

.layer-preview {
  max-height: 200px;
  object-fit: contain;
  border-radius: 4px;
}

.layer-preview-container {
  background-color: #f8f9fa;
  background-image: linear-gradient(45deg, #e9ecef 25%, transparent 25%),
    linear-gradient(-45deg, #e9ecef 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e9ecef 75%),
    linear-gradient(-45deg, transparent 75%, #e9ecef 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
}

.layer-card {
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &.border-primary {
    border-width: 2px;
  }
}

.opacity-50 {
  opacity: 0.5;
}

.layer-info {
  .badge {
    margin-right: 4px;
  }
}

.header-toggles {
  gap: 16px;

  ::v-deep .custom-control-label {
    font-size: 13px;
    font-weight: 400;
    white-space: nowrap;
  }
}
</style>
